import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Download,
  RefreshCw,
  ArrowLeft,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useImportStore } from "../../store/useImportStore";
import { useAppStore } from "../../store/useAppStore";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { formatDateTime, formatFileSize } from "../../utils/formatters";
import { apiService } from "../../services/api";
import {
  formatProcessingRate,
  formatProcessingTime,
} from "../../utils/importUtils";
import type { ImportError } from "../../types/import";

export const ImportResults: React.FC = () => {
  const { currentJob, currentFile, resetImport, setCurrentStep } =
    useImportStore();
  const { setCurrentView } = useAppStore();

  const [errors, setErrors] = useState<ImportError[]>([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorLimit, setErrorLimit] = useState(50);

  // Fetch import errors if job failed or has errors
  useEffect(() => {
    if (currentJob && currentJob.failed_rows > 0) {
      fetchErrors();
    }
  }, [currentJob]);

  const fetchErrors = async (): Promise<void> => {
    if (!currentJob) return;

    try {
      setLoadingErrors(true);
      const response = await apiService.getImportErrors(currentJob.id, {
        limit: errorLimit,
      });
      setErrors(response.errors || []);
    } catch (error) {
      console.error("Error fetching import errors:", error);
    } finally {
      setLoadingErrors(false);
    }
  };

  const handleStartNewImport = (): void => {
    resetImport();
    setCurrentStep("upload");
  };

  const handleViewDashboard = (): void => {
    // Navigate to dashboard or map view
    setCurrentView("dashboard");
  };

  const downloadErrorReport = (): void => {
    if (errors.length === 0) return;

    const csvContent = [
      ["Row Number", "Error Type", "Error Message", "Raw Data"],
      ...errors.map((error) => [
        error.row_number,
        error.error_type,
        error.error_message.replace(/"/g, '""'), // Escape quotes
        JSON.stringify(error.raw_data).replace(/"/g, '""'),
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `import_errors_${currentJob?.id}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadMoreErrors = async (): Promise<void> => {
    const newLimit = errorLimit + 50;
    setErrorLimit(newLimit);
    await fetchErrors();
  };

  if (!currentJob || !currentFile) {
    return (
      <div className="p-8">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Import Job Found
          </h2>
          <p className="text-gray-600 mb-6">
            Unable to find the current import job details.
          </p>
          <button
            onClick={handleStartNewImport}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Start New Import
          </button>
        </div>
      </div>
    );
  }

  const isSuccess =
    currentJob.status === "completed" && currentJob.failed_rows === 0;
  const isPartialSuccess =
    currentJob.status === "completed" && currentJob.failed_rows > 0;
  const isFailed = currentJob.status === "failed";

  return (
    <div className="p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          {isSuccess && <CheckCircle className="w-16 h-16 text-green-500" />}
          {isPartialSuccess && (
            <AlertTriangle className="w-16 h-16 text-yellow-500" />
          )}
          {isFailed && <XCircle className="w-16 h-16 text-red-500" />}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isSuccess && "Import Completed Successfully!"}
          {isPartialSuccess && "Import Completed with Warnings"}
          {isFailed && "Import Failed"}
        </h2>

        <p className="text-gray-600">
          {isSuccess &&
            "All GPS data has been imported successfully into the system."}
          {isPartialSuccess &&
            "Most data was imported, but some rows encountered errors."}
          {isFailed &&
            "The import process encountered critical errors and could not complete."}
        </p>
      </div>

      {/* Import Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Import Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-600">File Name</p>
            <p className="text-lg font-semibold text-gray-900 truncate">
              {currentFile.name}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-600">File Size</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatFileSize(currentJob.file_size)}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-600">Total Rows</p>
            <p className="text-lg font-semibold text-gray-900">
              {currentJob.total_rows.toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-600">Processing Time</p>
            <p className="text-lg font-semibold text-gray-900">
              {currentJob.end_time
                ? formatProcessingTime(
                    currentJob.start_time,
                    currentJob.end_time
                  )
                : "N/A"}
            </p>
          </div>
        </div>

        {/* Results Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Successful Rows
                </p>
                <p className="text-2xl font-bold text-green-900">
                  {currentJob.successful_rows.toLocaleString()}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-sm text-green-700 mt-2">
              {(
                (currentJob.successful_rows / currentJob.total_rows) *
                100
              ).toFixed(1)}
              % success rate
            </p>
          </div>

          {currentJob.failed_rows > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Failed Rows
                  </p>
                  <p className="text-2xl font-bold text-red-900">
                    {currentJob.failed_rows.toLocaleString()}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-sm text-red-700 mt-2">
                {(
                  (currentJob.failed_rows / currentJob.total_rows) *
                  100
                ).toFixed(1)}
                % error rate
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Processing Rate
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatProcessingRate(
                    currentJob.total_rows,
                    currentJob.start_time,
                    currentJob.end_time
                  )}
                </p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-500" />
            </div>
            <p className="text-sm text-blue-700 mt-2">rows per second</p>
          </div>
        </div>
      </div>

      {/* Error Details */}
      {currentJob.failed_rows > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
              Import Errors ({currentJob.failed_rows} rows)
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center"
              >
                <Eye className="w-4 h-4 mr-1" />
                {showErrors ? "Hide" : "Show"} Errors
                {showErrors ? (
                  <ChevronUp className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </button>
              {errors.length > 0 && (
                <button
                  onClick={downloadErrorReport}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Report
                </button>
              )}
            </div>
          </div>

          {showErrors && (
            <div className="border border-gray-200 rounded-lg">
              {loadingErrors ? (
                <div className="p-8 text-center">
                  <LoadingSpinner size="lg" className="mb-4" />
                  <p className="text-gray-600">Loading error details...</p>
                </div>
              ) : errors.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                            Row
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                            Error Type
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                            Message
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                            Raw Data
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {errors.map((error, index) => (
                          <tr
                            key={error.id}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {error.row_number}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  error.error_type === "validation"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : error.error_type === "processing"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {error.error_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {error.error_message}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                              <div className="max-w-xs truncate">
                                {JSON.stringify(error.raw_data)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {errors.length >= errorLimit && (
                    <div className="p-4 border-t border-gray-200 text-center">
                      <button
                        onClick={loadMoreErrors}
                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        Load More Errors
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No error details available
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Job Metadata */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Job Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Job ID:</span>
            <span className="ml-2 text-gray-600 font-mono">
              {currentJob.id}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Status:</span>
            <span
              className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                currentJob.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : currentJob.status === "failed"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {currentJob.status}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Started:</span>
            <span className="ml-2 text-gray-600">
              {formatDateTime(currentJob.start_time)}
            </span>
          </div>
          {currentJob.end_time && (
            <div>
              <span className="font-medium text-gray-700">Completed:</span>
              <span className="ml-2 text-gray-600">
                {formatDateTime(currentJob.end_time)}
              </span>
            </div>
          )}
        </div>

        {currentJob.error_message && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm font-medium text-red-800">Error Message:</p>
            <p className="text-sm text-red-700 mt-1">
              {currentJob.error_message}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={handleViewDashboard}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
        >
          <Eye className="w-5 h-5 mr-2" />
          View Dashboard
        </button>

        <button
          onClick={handleStartNewImport}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Import Another File
        </button>
      </div>
    </div>
  );
};
